import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useExclusiveAudio } from '../utils/useExclusiveAudio';
import { audioCoordinator } from '../utils/audioCoordinator';
import { motion } from 'framer-motion';
import { FaVolumeMute, FaVolumeUp } from 'react-icons/fa';

// Tess hologram — the same 3-layer parallax card as the showcase demo,
// scaled down for the About column: tilt on cursor, 3 rotating videos, sound toggle.
const VIDEOS = [
  'https://storage.googleapis.com/lupatest/Tara-Video.mp4',
  'https://storage.googleapis.com/lupatest/Tara-Video2.mp4',
  'https://storage.googleapis.com/lupatest/Tara-Video3.mp4',
];

function AboutHologram() {
  const [videoIndex, setVideoIndex] = useState(0);
  const { videoRef, setVideoRef, isMuted: muted, blocked, toggleMute } = useExclusiveAudio();
  const cardRef = useRef(null);
  const bgRef = useRef(null);
  const charRef = useRef(null);
  const fgRef = useRef(null);
  const shineRef = useRef(null);

  // Cycle through the three Tara videos.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    let timer;
    const onEnd = () => {
      timer = setTimeout(() => { if (audioCoordinator.getVoice() === null) setVideoIndex((i) => (i + 1) % VIDEOS.length); }, 1500);
    };
    video.addEventListener('ended', onEnd);
    return () => { clearTimeout(timer); video.removeEventListener('ended', onEnd); };
  }, [videoIndex, videoRef]);

  const onMove = useCallback((e) => {
    const card = cardRef.current;
    if (!card) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const rect = card.getBoundingClientRect();
    const nx = Math.max(-1, Math.min(1, (clientX - (rect.left + rect.width / 2)) / (rect.width / 2)));
    const ny = Math.max(-1, Math.min(1, (clientY - (rect.top + rect.height / 2)) / (rect.height / 2)));
    card.style.transform = `rotateX(${-ny * 12}deg) rotateY(${nx * 12}deg)`;
    if (bgRef.current) bgRef.current.style.transform = `translate(${nx * -8}px, ${ny * -8}px) scale(1.1)`;
    if (charRef.current) charRef.current.style.transform = `translate(${nx * -15}px, ${ny * -15}px) scale(1.05)`;
    if (fgRef.current) fgRef.current.style.transform = `translate(${nx * -25}px, ${ny * -25}px) scale(1.15)`;
    if (shineRef.current) {
      const sx = ((clientX - rect.left) / rect.width) * 100;
      const sy = ((clientY - rect.top) / rect.height) * 100;
      shineRef.current.style.background = `radial-gradient(circle at ${sx}% ${sy}%, rgba(255, 138, 61,0.28) 0%, rgba(255, 138, 61,0.08) 30%, transparent 70%)`;
    }
  }, []);

  const onLeave = useCallback(() => {
    const card = cardRef.current;
    if (!card) return;
    card.style.transform = 'rotateX(0deg) rotateY(0deg)';
    if (bgRef.current) bgRef.current.style.transform = 'translate(0,0) scale(1.1)';
    if (charRef.current) charRef.current.style.transform = 'translate(0,0) scale(1.05)';
    if (fgRef.current) fgRef.current.style.transform = 'translate(0,0) scale(1.15)';
    if (shineRef.current) shineRef.current.style.background = 'transparent';
  }, []);

  return (
    <div
      className="about-holo-scene"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onTouchMove={onMove}
      onTouchEnd={onLeave}
    >
      <div className="about-holo" ref={cardRef} aria-label="Tess — UBOTIKA's AI receptionist, rendered as a hologram">
        <div className="about-holo-layer about-holo-bg" ref={bgRef}>
          <img src="/hologram/bg-760.jpg" alt="" aria-hidden="true" loading="lazy" draggable="false" />
        </div>
        <div className="about-holo-layer about-holo-char" ref={charRef}>
          <video
            ref={setVideoRef}
            src={VIDEOS[videoIndex]}
            autoPlay={!blocked}
            muted={muted}
            playsInline
            key={videoIndex}
          />
        </div>
        <div className="about-holo-layer about-holo-fg" ref={fgRef}>
          <img src="/hologram/fg-760.png" alt="" aria-hidden="true" loading="lazy" draggable="false" />
        </div>
        <div className="about-holo-shine" ref={shineRef} />
        <button
          type="button"
          className="about-holo-mute"
          onClick={toggleMute}
          disabled={blocked}
          title={blocked ? "Close the voice demo to listen to Tess" : undefined}
          aria-label={muted ? 'Unmute Tess' : 'Mute Tess'}
        >
          {muted ? <FaVolumeMute /> : <FaVolumeUp />}
        </button>
      </div>
    </div>
  );
}

function About() {
  return (
    <section className="about" id="about">
      <div className="container">
        <div className="about-content">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <span className="studio-eyebrow">The human side of AI</span>
            <h2>More time for the work<br />only you can do.</h2>
            <p>
              You didn't start your business to spend all day answering phones,
              chasing down no-shows, or re-typing the same customer info into three
              different systems. We get it. Ubotika handles the repetitive stuff so
              you can focus on what you're actually good at — running your business.
            </p>
            <p>
              Our AI answers your phones 24/7, books appointments, follows up with
              customers, and keeps everything organized — automatically. No apps to
              learn, no software to manage. We build it, we set it up, and we keep
              it running. Most of our clients pay for themselves within 90 days and
              save over $17,000 a year.
            </p>
          </motion.div>
          <motion.div
            className="about-image"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <AboutHologram />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default About;
